import { useToastsStore } from '@/stores/toasts'
import type { TrailToastProps } from '@/components/TrailDS/Toast.vue'

type TrailToastPayload = Omit<TrailToastProps, 'type' | 'open'>

export function useTrailToast() {
  const MAX_TOASTS_SHOWN = 1
  const toastsStore = useToastsStore()

  const toast = async (options: Omit<TrailToastProps, 'open'>) => {
    // Toasts are client-only UI: their `action.onClick` is a function, and
    // creating one during SSR would put a function into Pinia state that Nuxt
    // then fails to serialize into the payload ("Cannot stringify a function").
    if (import.meta.server) return

    const id = toastsStore.nextToastId++

    if (toastsStore.toasts.length > MAX_TOASTS_SHOWN - 1) {
      const firstToast = toastsStore.toasts[0]
      if (firstToast) {
        await toastsStore.removeToast(firstToast.id)
      }
    }

    toastsStore.toasts.push({
      id,
      open: true,
      ...options,
    })
  }

  const normal = (options: TrailToastPayload) => {
    toast({ type: 'normal', ...options })
  }

  const positive = (options: TrailToastPayload) => {
    toast({ type: 'positive', ...options })
  }

  const negative = (options: TrailToastPayload) => {
    toast({ type: 'negative', ...options })
  }

  const warning = (options: TrailToastPayload) => {
    toast({ type: 'warning', ...options })
  }

  return {
    normal,
    positive,
    negative,
    warning,
  }
}
